import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Stethoscope,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Save,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDoctors = () => {
  const { token } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    photo: '',
    specialization: '',
    qualification: '',
    bio: '',
    department_id: '',
    availability_status: 'available',
    consultation_hours: '',
    profile_status: 'active'
  });

  const fetchDoctors = async () => {
    try {
      const response = await fetch(`${API}/api/admin/doctors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDoctors(data);
      }
    } catch (error) {
      console.error('Failed to fetch doctors:', error);
      toast.error('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch(`${API}/api/admin/departments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  useEffect(() => {
    fetchDoctors();
    fetchDepartments();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingDoctor 
        ? `${API}/api/admin/doctors/${editingDoctor.id}`
        : `${API}/api/admin/doctors`;
      
      const response = await fetch(url, {
        method: editingDoctor ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingDoctor ? 'Doctor updated' : 'Doctor created');
        setShowModal(false);
        setEditingDoctor(null);
        resetForm();
        fetchDoctors();
      } else {
        throw new Error('Operation failed');
      }
    } catch (error) {
      toast.error('Failed to save doctor');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this doctor?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/doctors/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Doctor deleted');
        fetchDoctors();
      }
    } catch (error) {
      toast.error('Failed to delete doctor');
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      photo: '',
      specialization: '',
      qualification: '',
      bio: '',
      department_id: '',
      availability_status: 'available',
      consultation_hours: '',
      profile_status: 'active'
    });
  };

  const openEditModal = (doctor) => {
    setEditingDoctor(doctor);
    setFormData({
      full_name: doctor.full_name,
      photo: doctor.photo || '',
      specialization: doctor.specialization,
      qualification: doctor.qualification,
      bio: doctor.bio,
      department_id: doctor.department_id,
      availability_status: doctor.availability_status,
      consultation_hours: doctor.consultation_hours,
      profile_status: doctor.profile_status
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingDoctor(null);
    resetForm();
    setShowModal(true);
  };

  const filteredDoctors = doctors.filter(doc =>
    doc.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.specialization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.department_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      available: 'bg-green-100 text-green-700',
      unavailable: 'bg-red-100 text-red-700',
      on_leave: 'bg-yellow-100 text-yellow-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
              <Stethoscope className="w-7 h-7 text-primary" />
              Doctors Management
            </h1>
            <p className="text-slate-600">Manage doctor profiles and availability</p>
          </div>
          <Button onClick={openCreateModal} className="bg-primary hover:bg-primary-700" data-testid="add-doctor-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Doctor
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search doctors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="doctor-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Doctors Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full p-8 text-center text-slate-500">Loading...</div>
          ) : filteredDoctors.length === 0 ? (
            <div className="col-span-full p-8 text-center text-slate-500">No doctors found</div>
          ) : (
            filteredDoctors.map((doctor) => (
              <Card key={doctor.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex-shrink-0 overflow-hidden">
                      {doctor.photo ? (
                        <img src={doctor.photo} alt={doctor.full_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">{doctor.full_name}</h3>
                      <p className="text-sm text-primary">{doctor.specialization}</p>
                      <p className="text-xs text-slate-500">{doctor.department_name}</p>
                      <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(doctor.availability_status)}`}>
                        {doctor.availability_status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <Button size="sm" variant="outline" onClick={() => openEditModal(doctor)} data-testid={`edit-doctor-${doctor.id}`}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => handleDelete(doctor.id)}
                      data-testid={`delete-doctor-${doctor.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingDoctor ? 'Edit Doctor' : 'Add Doctor'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        required
                        data-testid="doctor-name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="specialization">Specialization *</Label>
                      <Input
                        id="specialization"
                        value={formData.specialization}
                        onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                        required
                        data-testid="doctor-specialization-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="department_id">Department *</Label>
                      <select
                        id="department_id"
                        value={formData.department_id}
                        onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        required
                        data-testid="doctor-department-select"
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="qualification">Qualification *</Label>
                      <Input
                        id="qualification"
                        value={formData.qualification}
                        onChange={(e) => setFormData({...formData, qualification: e.target.value})}
                        placeholder="e.g., MD, MBBS, PhD"
                        required
                        data-testid="doctor-qualification-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="consultation_hours">Consultation Hours *</Label>
                      <Input
                        id="consultation_hours"
                        value={formData.consultation_hours}
                        onChange={(e) => setFormData({...formData, consultation_hours: e.target.value})}
                        placeholder="e.g., Mon-Fri 9AM-5PM"
                        required
                        data-testid="doctor-hours-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="availability_status">Availability</Label>
                      <select
                        id="availability_status"
                        value={formData.availability_status}
                        onChange={(e) => setFormData({...formData, availability_status: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        data-testid="doctor-availability-select"
                      >
                        <option value="available">Available</option>
                        <option value="unavailable">Unavailable</option>
                        <option value="on_leave">On Leave</option>
                      </select>
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="photo">Photo URL</Label>
                      <Input
                        id="photo"
                        value={formData.photo}
                        onChange={(e) => setFormData({...formData, photo: e.target.value})}
                        placeholder="https://..."
                        data-testid="doctor-photo-input"
                      />
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="bio">Biography *</Label>
                      <textarea
                        id="bio"
                        value={formData.bio}
                        onChange={(e) => setFormData({...formData, bio: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        rows={4}
                        required
                        data-testid="doctor-bio-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="profile_status">Profile Status</Label>
                      <select
                        id="profile_status"
                        value={formData.profile_status}
                        onChange={(e) => setFormData({...formData, profile_status: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        data-testid="doctor-profile-status-select"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="bg-primary hover:bg-primary-700" data-testid="doctor-save-btn">
                      <Save className="w-4 h-4 mr-2" />
                      {editingDoctor ? 'Update' : 'Create'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDoctors;
